import React, { useEffect, useMemo } from 'react';
import { IAccreditationFormValues } from '../../../../@types/model/masterData/accreditation/accreditationFormValues';
import Button from '@mui/material/Button';
import { useAppSelector } from '../../../../@types/redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ControlledTextField, ControlledMuiAutoCompleteSelect, PillButton, ControlledDateInput } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import AccreditationModelHelper from '../../../../@types/model/masterData/accreditation/accreditationModelHelper';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface IAccreditationFormProps {
    isLoading : boolean;
    initialValues : IAccreditationFormValues;
    onSubmit : (values : IAccreditationFormValues) => void;
    onClose : () => void;
}

const AccreditationForm = (props : IAccreditationFormProps) : React.ReactElement => {
    const farms = useAppSelector(x => x.masterData.farms);

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<IAccreditationFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(AccreditationModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const farmOptions = useMemo(() => {
        return farms
            .filter(x => x.isActive)
            .map(x => ({ label: `(${x.code}) ${x.name}`, value: x.id })); // TODO: OptionType.FromDataModel
    }, [farms]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p30'}>
                    <ControlledTextField
                        label={'Code'}
                        name={'code'}
                        control={control}
                        defaultValue={props.initialValues.code}
                        className={'mb10'}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb10'}
                        control={control}
                        label={'Name'}
                        defaultValue={props.initialValues.name}
                        name={'name'}
                        size={'small'}
                    />
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <ControlledDateInput
                            className={'p0 mb10'}
                            control={control}
                            label={'Issue Date'}
                            defaultValue={props.initialValues.issueDate}
                            name={'issueDate'}
                        />
                        <ControlledDateInput
                            className={'p0 mb10'}
                            control={control}
                            label={'Expiration Date'}
                            defaultValue={props.initialValues.expirationDate}
                            name={'expirationDate'}
                        />
                    </LocalizationProvider>
                    <ControlledMuiAutoCompleteSelect
                        label={'Farms'}
                        name={'farms'}
                        control={control}
                        className={'p0 mb10'}
                        isMulti
                        defaultValues={props.initialValues.farms}
                        options={farmOptions}
                    />
                    <div className={'flx1 fdr jcfs'}>
                        <ControlledLabelledCheckBox 
                            name={'isActive'}
                            label={'Is Active?'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                            disabled={props.initialValues.isActive}
                        />
                    </div>
                    <div className={'fdr flx1 aic jcfe mb20'}>
                        <Button 
                            className={'cpd mr20'}
                            onClick={props.onClose} 
                            type={'reset'}
                        >
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={ !isDirty || !isValid || props.isLoading }
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AccreditationForm;
